import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TaskUI, AttributeDef, TaskStateProfile, deriveTaskStateMode } from '@flexiplanner/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, CalendarClock, Trash2, Settings, Loader2, Play, Timer } from 'lucide-react';
import { api } from '@/lib/api';
import { formatMissingPlanningFields, getMissingPlanningFields } from '@/lib/activityReadiness';
import { parseAttributeOptions } from '@/lib/attributeOptions';
import { EnumAttributeInput } from '@/components/attributes/EnumAttributeInput';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { useScheduleStore } from '@/stores/scheduleStore';
import { useTasksStore } from '@/stores/tasksStore';
import { ActivitySessionLogger } from '@/components/activities/ActivitySessionLogger';

interface TaskEditorProps {
  task: TaskUI | null;
  open: boolean;
  onClose: () => void;
  onSave: (task: Partial<TaskUI>) => void;
  onDelete?: () => void;
  onRescheduleTomorrow?: () => void;
}

interface AttributeDraft {
  name: string;
  data_type: 'float' | 'enum';
  specificity: number;
  field_weight: number;
  custom_math_map: string;
  options: string;
  min_val: string;
  max_val: string;
  tensor_index: number;
  required: boolean;
}

const ACTIVITY_ORDER_ATTR = 'depends_on_activity_ids';

export function TaskEditor({ task, open, onClose, onSave, onDelete, onRescheduleTomorrow }: TaskEditorProps) {
  const { bundles, loadWorkspace, getBundle, getProject } = useWorkspaceStore();
  const startImmersionMode = useScheduleStore(s => s.startImmersionMode);
  const allTasks = useTasksStore(s => s.tasks);
  const updateTask = useTasksStore(s => s.updateTask);
  const activeBundles = useMemo(
    () => bundles.filter(bundle => bundle.status === 'active'),
    [bundles]
  );
  const [formData, setFormData] = useState<Partial<TaskUI>>({
    title: task?.title || '',
    description: task?.description || '',
    bundleId: task?.bundleId || null,
    priority: task?.priority || 3,
    estimatedDuration: task?.estimatedDuration || 60,
    deadline: task?.deadline || undefined,
    stateProfile: task?.stateProfile || {
      mode: 'neutral',
      required_productivity_energy: null,
      required_rejuvenation_energy: null,
      productivity_return: null,
      rejuvenation_return: null,
      resistance: null,
    },
    customAttributes: task?.customAttributes || {},
    scheduledStart: task?.scheduledStart || null,
    scheduledEnd: task?.scheduledEnd || null,
  });

  const [availableAttributes, setAvailableAttributes] = useState<AttributeDef[]>([]);
  const [isManagingFields, setIsManagingFields] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState<'float' | 'enum'>('float');
  const [newFieldSpecificity, setNewFieldSpecificity] = useState(5.0);
  const [newFieldWeight, setNewFieldWeight] = useState(1.0);
  const [newFieldMathMap, setNewFieldMathMap] = useState('');
  const [newFieldOptions, setNewFieldOptions] = useState('');
  const [newFieldMin, setNewFieldMin] = useState('');
  const [newFieldMax, setNewFieldMax] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [editingAttributeId, setEditingAttributeId] = useState<string | null>(null);
  const [editingAttributeDraft, setEditingAttributeDraft] = useState<AttributeDraft | null>(null);
  const [savingAttributeId, setSavingAttributeId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingField, setIsCreatingField] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoggingSession, setIsLoggingSession] = useState(false);
  const [orderSelectTaskId, setOrderSelectTaskId] = useState('');

  useEffect(() => {
    if (open) {
      fetchAttributes();
      loadWorkspace().catch(error => console.error('Failed to load workspace', error));
    }
  }, [open, loadWorkspace]);

  useEffect(() => {
    if (!open) return;
    setFormError(null);
    setFormData({
      title: task?.title || '',
      description: task?.description || '',
      bundleId: task?.bundleId || (activeBundles.length === 1 ? activeBundles[0]!.id : null),
      priority: task?.priority || 3,
      estimatedDuration: task?.estimatedDuration || 30,
      deadline: task?.deadline,
      stateProfile: task?.stateProfile || {
        mode: 'neutral',
        required_productivity_energy: null,
        required_rejuvenation_energy: null,
        productivity_return: null,
        rejuvenation_return: null,
        resistance: null,
      },
      customAttributes: task?.customAttributes || {},
      scheduledStart: task?.scheduledStart || null,
      scheduledEnd: task?.scheduledEnd || null,
    });
    setOrderSelectTaskId('');
    // Workspace reloads update activeBundles; they should not erase edits in progress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, open]);

  const fetchAttributes = async () => {
    try {
      const response = await api.user.getAttributes();
      if (response.success && response.data) {
        setAvailableAttributes(response.data);
      }
    } catch (e) {
      console.error('Failed to fetch attributes', e);
    }
  };

  const missingRequiredFields = useMemo(
    () => getMissingPlanningFields(formData, availableAttributes),
    [formData, availableAttributes]
  );
  const missingRequiredLabel = formatMissingPlanningFields(missingRequiredFields);

  useEffect(() => {
    if (missingRequiredFields.length === 0 && formError) {
      setFormError(null);
    }
  }, [missingRequiredFields.length, formError]);

  const canLogActivitySession = Boolean(
    task &&
      hasUsableDate(task.scheduledStart) &&
      hasUsableDate(task.scheduledEnd)
  );
  const canEditScheduledTime = Boolean(
    task &&
      (hasUsableDate(formData.scheduledStart) || hasUsableDate(formData.scheduledEnd))
  );
  const prerequisiteIds = useMemo(
    () => normalizeDependencyIds(formData.customAttributes?.[ACTIVITY_ORDER_ATTR]),
    [formData.customAttributes]
  );
  const prerequisiteOptions = useMemo(
    () =>
      allTasks
        .filter(candidate => candidate.id !== task?.id && candidate.status !== 'cancelled')
        .sort((a, b) => (a.title || '').localeCompare(b.title || '')),
    [allTasks, task?.id]
  );
  const prerequisiteTasks = useMemo(
    () => prerequisiteIds.map(id => allTasks.find(candidate => candidate.id === id)).filter(Boolean) as TaskUI[],
    [allTasks, prerequisiteIds]
  );

  const handleSave = () => {
    if (missingRequiredFields.length > 0) {
      setFormError(`Fill ${missingRequiredLabel} before this activity can be scheduled.`);
      return;
    }
    if (
      hasUsableDate(formData.scheduledStart) &&
      hasUsableDate(formData.scheduledEnd) &&
      new Date(formData.scheduledEnd!).getTime() <= new Date(formData.scheduledStart!).getTime()
    ) {
      setFormError('Scheduled end must be after scheduled start.');
      return;
    }
    setIsSaving(true);
    try {
      onSave({
        ...formData,
        customAttributes: {
          ...(formData.customAttributes || {}),
          needs_review: false,
          required_fields_complete: true,
          missing_required_fields: [],
        },
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleAttributeChange = (attrId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      customAttributes: {
        ...(prev.customAttributes || {}),
        [attrId]: value,
      },
    }));
  };

  const handleDurationChange = (value: string) => {
    const duration = parseInt(value, 10) || 0;
    setFormData(prev => ({
      ...prev,
      estimatedDuration: duration,
      scheduledEnd:
        duration > 0 && hasUsableDate(prev.scheduledStart)
          ? addMinutesIso(prev.scheduledStart!, duration)
          : prev.scheduledEnd,
    }));
  };

  const handleScheduledStartChange = (value: string) => {
    const nextStart = dateTimeLocalToIso(value);
    setFormData(prev => ({
      ...prev,
      scheduledStart: nextStart,
      scheduledEnd:
        nextStart && Number(prev.estimatedDuration || 0) > 0
          ? addMinutesIso(nextStart, Number(prev.estimatedDuration))
          : nextStart
            ? prev.scheduledEnd
            : null,
    }));
  };

  const handleScheduledEndChange = (value: string) => {
    const nextEnd = dateTimeLocalToIso(value);
    setFormData(prev => {
      const startMs = prev.scheduledStart ? new Date(prev.scheduledStart).getTime() : NaN;
      const endMs = nextEnd ? new Date(nextEnd).getTime() : NaN;
      const nextDuration =
        Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs
          ? Math.max(1, Math.round((endMs - startMs) / 60000))
          : prev.estimatedDuration;
      return {
        ...prev,
        scheduledEnd: nextEnd,
        estimatedDuration: nextDuration,
      };
    });
  };

  const setPrerequisiteIds = (ids: string[]) => {
    setFormData(prev => ({
      ...prev,
      customAttributes: {
        ...(prev.customAttributes || {}),
        [ACTIVITY_ORDER_ATTR]: Array.from(new Set(ids.filter(Boolean))),
      },
    }));
  };

  const addPrerequisite = (id: string) => {
    if (!id) return;
    setPrerequisiteIds([...prerequisiteIds, id]);
    setOrderSelectTaskId('');
  };

  const handleStateProfileChange = (key: StateScoreKey, value: string) => {
    setFormData(prev => ({
      ...prev,
      stateProfile: deriveProfileUpdate(prev.stateProfile, key, value),
    }));
  };

  const createField = async () => {
    if (!newFieldName.trim()) return;
    setIsCreatingField(true);
    try {
      const response = await api.user.addAttribute({
        name: newFieldName,
        data_type: newFieldType,
        specificity: newFieldSpecificity,
        field_weight: newFieldWeight,
        custom_math_map: newFieldMathMap || null,
        options: newFieldOptions,
        min_val: newFieldMin === '' ? null : Number(newFieldMin),
        max_val: newFieldMax === '' ? null : Number(newFieldMax),
        required: newFieldRequired,
      });
      if (response.success) {
        setNewFieldName('');
        setNewFieldMathMap('');
        setNewFieldOptions('');
        setNewFieldSpecificity(5.0);
        setNewFieldWeight(1.0);
        setNewFieldMin('');
        setNewFieldMax('');
        setNewFieldRequired(false);
        fetchAttributes();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsCreatingField(false);
    }
  };

  const deleteField = async (id: string) => {
    try {
      const response = await api.user.deleteAttribute(id);
      if (response.success) {
        fetchAttributes();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const startEditingField = (attr: AttributeDef) => {
    setEditingAttributeId(attr.attr_id);
    setEditingAttributeDraft({
      name: attr.name,
      data_type: attr.data_type === 'enum' ? 'enum' : 'float',
      specificity: Number(attr.specificity ?? 5),
      field_weight: Number(attr.field_weight ?? 1),
      custom_math_map: attr.custom_math_map || '',
      options: parseAttributeOptions(attr).join(', '),
      min_val: attr.min_val == null ? '' : String(attr.min_val),
      max_val: attr.max_val == null ? '' : String(attr.max_val),
      tensor_index: Number(attr.tensor_index || 0),
      required: Boolean(attr.required),
    });
  };

  const saveEditingField = async () => {
    if (!editingAttributeId || !editingAttributeDraft?.name.trim()) return;
    setSavingAttributeId(editingAttributeId);
    try {
      const response = await api.user.updateAttribute(editingAttributeId, {
        name: editingAttributeDraft.name,
        data_type: editingAttributeDraft.data_type === 'enum' ? 'enum' : 'float',
        specificity: editingAttributeDraft.specificity,
        field_weight: editingAttributeDraft.field_weight,
        custom_math_map: editingAttributeDraft.custom_math_map || null,
        options: editingAttributeDraft.options,
        min_val: editingAttributeDraft.min_val === '' ? null : Number(editingAttributeDraft.min_val),
        max_val: editingAttributeDraft.max_val === '' ? null : Number(editingAttributeDraft.max_val),
        tensor_index: editingAttributeDraft.tensor_index,
        required: editingAttributeDraft.required,
      });
      if (response.success) {
        setEditingAttributeId(null);
        setEditingAttributeDraft(null);
        fetchAttributes();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSavingAttributeId(null);
    }
  };

  const handleAttributeDefinitionUpdated = (updatedAttr: AttributeDef) => {
    setAvailableAttributes(current =>
      current.map(attr => (attr.attr_id === updatedAttr.attr_id ? updatedAttr : attr))
    );
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="!flex !h-dvh !max-h-dvh !w-dvw !max-w-none !translate-y-[-50%] flex-col overflow-hidden rounded-none border-0 p-0 sm:!h-auto sm:!max-h-[calc(100dvh-2rem)] sm:!w-[calc(100vw-1rem)] sm:!max-w-[760px] sm:rounded-lg sm:border sm:p-6">
        <DialogHeader className="shrink-0 border-b bg-background/96 px-4 pb-3 pt-[calc(1rem+env(safe-area-inset-top))] pr-12 text-left backdrop-blur sm:flex sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:border-0 sm:bg-transparent sm:p-0 sm:pr-8">
          <div className="min-w-0">
            <DialogTitle>{task ? 'Edit Activity' : 'New Activity'}</DialogTitle>
            <DialogDescription>
              {task ? 'Make changes to your activity' : 'Create a new activity'}
            </DialogDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsManagingFields(!isManagingFields)}
            className="w-full sm:w-auto"
          >
            <Settings className="mr-2 h-4 w-4" />
            {isManagingFields ? 'Done' : 'Manage Fields'}
          </Button>
        </DialogHeader>

        <div className="native-scroll min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-0 sm:py-0">
        {isManagingFields ? (
          <div className="my-2 grid gap-4 rounded-md border-b border-t bg-slate-50 p-4 py-4 dark:bg-slate-900">
            <h3 className="text-sm font-medium">Manage Custom Fields</h3>

            <div className="space-y-4">
              {availableAttributes.map(attr => {
                const isEditing = editingAttributeId === attr.attr_id && editingAttributeDraft;
                return (
                  <div
                    key={attr.attr_id}
                    className="rounded border bg-white p-3 shadow-sm dark:bg-slate-800"
                  >
                    {isEditing ? (
                      <div className="grid gap-3">
                        <div className="grid gap-3 sm:grid-cols-[1fr_120px_120px]">
                          <div>
                            <Label className="text-xs">Field Name</Label>
                            <Input
                              value={editingAttributeDraft.name}
                              onChange={event =>
                                setEditingAttributeDraft(current =>
                                  current ? { ...current, name: event.target.value } : current
                                )
                              }
                              className="h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Specificity</Label>
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              step="0.1"
                              value={editingAttributeDraft.specificity}
                              onChange={event =>
                                setEditingAttributeDraft(current =>
                                  current ? { ...current, specificity: Number(event.target.value) } : current
                                )
                              }
                              className="h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Weight</Label>
                            <Input
                              type="number"
                              min="0"
                              max="10"
                              step="0.1"
                              value={editingAttributeDraft.field_weight}
                              onChange={event =>
                                setEditingAttributeDraft(current =>
                                  current ? { ...current, field_weight: Number(event.target.value) } : current
                                )
                              }
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-[150px_1fr]">
                          <div>
                            <Label className="text-xs">Type</Label>
                            <Select
                              value={editingAttributeDraft.data_type}
                              onValueChange={(value: any) =>
                                setEditingAttributeDraft(current =>
                                  current ? { ...current, data_type: value } : current
                                )
                              }
                            >
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="float">Numeric</SelectItem>
                                <SelectItem value="enum">Dropdown</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {editingAttributeDraft.data_type === 'enum' ? (
                            <div>
                              <Label className="text-xs">Dropdown Options</Label>
                              <Input
                                value={editingAttributeDraft.options}
                                onChange={event =>
                                  setEditingAttributeDraft(current =>
                                    current ? { ...current, options: event.target.value } : current
                                  )
                                }
                                placeholder="Focused, Calm, Resistant"
                                className="h-8 text-sm"
                              />
                            </div>
                          ) : (
                            <div className="grid gap-3 sm:grid-cols-3">
                              <div>
                                <Label className="text-xs">Min</Label>
                                <Input
                                  type="number"
                                  value={editingAttributeDraft.min_val}
                                  onChange={event =>
                                    setEditingAttributeDraft(current =>
                                      current ? { ...current, min_val: event.target.value } : current
                                    )
                                  }
                                  placeholder="Auto"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Max</Label>
                                <Input
                                  type="number"
                                  value={editingAttributeDraft.max_val}
                                  onChange={event =>
                                    setEditingAttributeDraft(current =>
                                      current ? { ...current, max_val: event.target.value } : current
                                    )
                                  }
                                  placeholder="Auto"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Math Map</Label>
                                <Input
                                  value={editingAttributeDraft.custom_math_map}
                                  onChange={event =>
                                    setEditingAttributeDraft(current =>
                                      current ? { ...current, custom_math_map: event.target.value } : current
                                    )
                                  }
                                  placeholder="linear_pull"
                                  className="h-8 text-sm"
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        <label className="flex min-h-9 items-center gap-2 rounded-md border bg-muted/20 px-3 text-xs">
                          <Checkbox
                            checked={editingAttributeDraft.required}
                            onCheckedChange={value =>
                              setEditingAttributeDraft(current =>
                                current ? { ...current, required: Boolean(value) } : current
                              )
                            }
                          />
                          Required for scheduling
                        </label>

                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingAttributeId(null);
                              setEditingAttributeDraft(null);
                            }}
                            disabled={savingAttributeId === attr.attr_id}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={saveEditingField}
                            disabled={savingAttributeId === attr.attr_id}
                          >
                            {savingAttributeId === attr.attr_id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save Field
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium">{attr.name}</div>
                          <div className="text-xs text-slate-500">
                            {attr.data_type} | Specificity: {attr.specificity} | Weight:{' '}
                            {attr.field_weight ?? 1}
                            {Boolean(attr.required) ? ' | Required' : ' | Optional'}
                            {attr.min_val != null && attr.max_val != null ? ` | Range: ${attr.min_val}-${attr.max_val}` : ' | Auto range'}
                            {attr.options ? ` | ${parseAttributeOptions(attr).length} options` : ''}
                            {attr.custom_math_map ? ` | Map: ${attr.custom_math_map}` : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEditingField(attr)}
                            disabled={isCreatingField}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteField(attr.attr_id)}
                            disabled={isCreatingField}
                            className="text-red-500 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 grid gap-3 border-t pt-4">
              <h4 className="text-xs font-semibold uppercase text-slate-500">Add New Field</h4>
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Field Name</Label>
                  <Input
                    value={newFieldName}
                    onChange={e => setNewFieldName(e.target.value)}
                    placeholder="e.g. Energy, Emotion..."
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Specificity</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="10.0"
                    value={newFieldSpecificity}
                    onChange={e => setNewFieldSpecificity(parseFloat(e.target.value) || 1.0)}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Weight</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="10.0"
                    value={newFieldWeight}
                    onChange={e => setNewFieldWeight(parseFloat(e.target.value) || 1.0)}
                    className="h-8 text-sm"
                  />
                </div>
              </div>

              <div className="grid gap-3">
                <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_220px]">
                  <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={newFieldType} onValueChange={(v: any) => setNewFieldType(v)}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="float">Numeric</SelectItem>
                      <SelectItem value="enum">Dropdown</SelectItem>
                    </SelectContent>
                  </Select>
                  </div>
                  <label className="flex h-8 items-center gap-2 rounded-md border bg-muted/20 px-3 text-xs">
                    <Checkbox
                      checked={newFieldRequired}
                      onCheckedChange={value => setNewFieldRequired(Boolean(value))}
                    />
                    Required for scheduling
                  </label>
                </div>
              </div>

              {newFieldType === 'enum' && (
                <div>
                  <Label className="text-xs">Dropdown Options</Label>
                  <Input
                    value={newFieldOptions}
                    onChange={e => setNewFieldOptions(e.target.value)}
                    placeholder="Focused, Restorative, Admin"
                    className="h-8 text-sm"
                  />
                </div>
              )}

              {newFieldType === 'float' && (
                <div className="grid gap-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs">Min (optional)</Label>
                      <Input
                        type="number"
                        value={newFieldMin}
                        onChange={e => setNewFieldMin(e.target.value)}
                        placeholder="Auto"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Max (optional)</Label>
                      <Input
                        type="number"
                        value={newFieldMax}
                        onChange={e => setNewFieldMax(e.target.value)}
                        placeholder="Auto"
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Math Map (optional)</Label>
                    <Input
                      value={newFieldMathMap}
                      onChange={e => setNewFieldMathMap(e.target.value)}
                      placeholder="linear_pull or exponential_decay"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              )}
              <Button
                onClick={createField}
                className="mt-2 w-full"
                size="sm"
                disabled={isCreatingField}
              >
                {isCreatingField && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add Field
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 py-4">
            {(formError || missingRequiredFields.length > 0) && (
              <div className="rounded-md border border-amber-300/50 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-medium">
                      {formData.customAttributes?.source === 'canvas'
                        ? 'Canvas assignment needs planning details.'
                        : 'Activity needs planning details.'}
                    </p>
                    <p className="mt-1 text-xs">
                      {formError || `Required before Solve: ${missingRequiredLabel}.`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Core Fields */}
            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder="Activity title"
              />
            </div>

            <div className="grid gap-4 rounded-md border bg-muted/20 p-3 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Bundle</Label>
                {activeBundles.length > 0 ? (
                  <Select
                    value={formData.bundleId || ''}
                    onValueChange={value => setFormData({ ...formData, bundleId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a bundle" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeBundles.map(bundle => (
                        <SelectItem key={bundle.id} value={bundle.id}>
                          {bundle.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="rounded-md border bg-background p-3">
                    <p className="text-sm text-muted-foreground">
                      Create a bundle before saving activities.
                    </p>
                    <Button asChild variant="outline" size="sm" className="mt-2">
                      <Link to="/workspace" onClick={onClose}>
                        Create bundle
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
              <div className="grid gap-2">
                <Label>Project</Label>
                <div className="flex h-10 items-center rounded-md border bg-background px-3 text-sm text-muted-foreground">
                  {getProject(getBundle(formData.bundleId)?.projectId)?.name || 'No project'}
                </div>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={formData.estimatedDuration || ''}
                  onChange={e => handleDurationChange(e.target.value)}
                  min={5}
                  step={5}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="deadline">Intended Deadline</Label>
                <Input
                  id="deadline"
                  type="datetime-local"
                  value={
                    formData.deadline
                      ? new Date(
                          new Date(formData.deadline).getTime() -
                            new Date(formData.deadline).getTimezoneOffset() * 60000
                        )
                          .toISOString()
                          .slice(0, 16)
                      : ''
                  }
                  onChange={e => {
                    if (!e.target.value) {
                      setFormData({ ...formData, deadline: undefined });
                      return;
                    }
                    const d = new Date(e.target.value);
                    setFormData({ ...formData, deadline: d.toISOString() });
                  }}
                />
              </div>
            </div>

            {canEditScheduledTime && (
              <div className="grid gap-4 rounded-md border bg-muted/20 p-3 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="scheduled-start">Scheduled Start</Label>
                  <Input
                    id="scheduled-start"
                    type="datetime-local"
                    value={dateTimeLocalValue(formData.scheduledStart)}
                    onChange={event => handleScheduledStartChange(event.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="scheduled-end">Scheduled End</Label>
                  <Input
                    id="scheduled-end"
                    type="datetime-local"
                    value={dateTimeLocalValue(formData.scheduledEnd)}
                    onChange={event => handleScheduledEndChange(event.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="grid gap-4 border-t pt-4">
              <div>
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Actionable State
                </h4>
                <p className="text-xs text-muted-foreground">
                  P/RJ investment and return used by Solve.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label>Mode</Label>
                  <div className="flex h-10 items-center rounded-md border bg-muted/40 px-3 text-sm font-medium">
                    {formatMode(deriveTaskStateMode(formData.stateProfile))}
                  </div>
                </div>
                <StateScoreField
                  label="Resistance"
                  value={formData.stateProfile?.resistance}
                  onChange={value => handleStateProfileChange('resistance', value)}
                />
                <StateScoreField
                  label="Required P"
                  value={formData.stateProfile?.required_productivity_energy}
                  onChange={value => handleStateProfileChange('required_productivity_energy', value)}
                />
                <StateScoreField
                  label="Required RJ"
                  value={formData.stateProfile?.required_rejuvenation_energy}
                  onChange={value => handleStateProfileChange('required_rejuvenation_energy', value)}
                />
                <StateScoreField
                  label="P Return"
                  value={formData.stateProfile?.productivity_return}
                  onChange={value => handleStateProfileChange('productivity_return', value)}
                />
                <StateScoreField
                  label="RJ Return"
                  value={formData.stateProfile?.rejuvenation_return}
                  onChange={value => handleStateProfileChange('rejuvenation_return', value)}
                />
              </div>
            </div>

            {/* Custom Attributes Fields */}
            {availableAttributes.length > 0 && (
              <div className="mt-4 grid gap-4 border-t pt-4">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  Custom Attributes
                </h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  {availableAttributes.map(attr => (
                    <div key={attr.attr_id} className="grid gap-2">
                      <Label htmlFor={`attr-${attr.attr_id}`}>{attr.name}</Label>
                      {attr.data_type === 'enum' ? (
                        <EnumAttributeInput
                          attr={attr}
                          value={formData.customAttributes?.[attr.attr_id] ?? ''}
                          onChange={value => handleAttributeChange(attr.attr_id, value)}
                          onAttributeUpdated={handleAttributeDefinitionUpdated}
                          triggerId={`attr-${attr.attr_id}`}
                        />
                      ) : ['Number', 'float', 'integer'].includes(attr.data_type) ? (
                        <Input
                          id={`attr-${attr.attr_id}`}
                          type="number"
                          value={formData.customAttributes?.[attr.attr_id] ?? ''}
                          onChange={e =>
                            handleAttributeChange(
                              attr.attr_id,
                              e.target.value === '' ? '' : parseFloat(e.target.value)
                            )
                          }
                          placeholder="Numeric value..."
                        />
                      ) : (
                        <Input
                          id={`attr-${attr.attr_id}`}
                          type="text"
                          value={formData.customAttributes?.[attr.attr_id] ?? ''}
                          onChange={e => handleAttributeChange(attr.attr_id, e.target.value)}
                          placeholder="String value..."
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {task && (
              <div className="mt-4 grid gap-3 border-t pt-4">
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Activity Orders
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Activities selected here must finish before this one can be scheduled.
                  </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                  <Select value={orderSelectTaskId} onValueChange={value => addPrerequisite(value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Add a prerequisite activity" />
                    </SelectTrigger>
                    <SelectContent>
                      {prerequisiteOptions
                        .filter(candidate => !prerequisiteIds.includes(candidate.id))
                        .map(candidate => (
                          <SelectItem key={candidate.id} value={candidate.id}>
                            {candidate.title || 'Untitled activity'}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                {prerequisiteTasks.length > 0 ? (
                  <div className="grid gap-2">
                    {prerequisiteTasks.map(prerequisite => (
                      <div
                        key={prerequisite.id}
                        className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2 text-sm"
                      >
                        <span className="min-w-0 truncate">{prerequisite.title || 'Untitled activity'}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setPrerequisiteIds(prerequisiteIds.filter(id => id !== prerequisite.id))}
                          aria-label={`Remove ${prerequisite.title || 'activity'} prerequisite`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                    No prerequisite order set.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        </div>

        <DialogFooter className="shrink-0 gap-3 border-t bg-background/96 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:mt-6 sm:flex sm:items-center sm:justify-between sm:border-0 sm:bg-transparent sm:p-0">
          <div className="flex flex-wrap gap-2">
            {task && onDelete && !isManagingFields && (
              <Button variant="destructive" onClick={onDelete} disabled={isSaving}>
                Delete Activity
              </Button>
            )}
            {task && onRescheduleTomorrow && !isManagingFields && (
              <Button variant="outline" onClick={onRescheduleTomorrow} disabled={isSaving}>
                <CalendarClock className="mr-2 h-4 w-4" />
                Tomorrow
              </Button>
            )}
            {task && !isManagingFields && (
              <Button
                type="button"
                onClick={() => {
                  if (missingRequiredFields.length > 0) {
                    setFormError(`Fill ${missingRequiredLabel} before starting this activity.`);
                    return;
                  }
                  startImmersionMode(task.id);
                  onClose();
                }}
                disabled={isSaving || missingRequiredFields.length > 0}
                title={missingRequiredFields.length > 0 ? `Missing ${missingRequiredLabel}` : 'Start activity'}
                className="bg-cyan-300 text-slate-950 hover:bg-cyan-200"
              >
                <Play className="mr-2 h-4 w-4 fill-current" />
                Start Activity
              </Button>
            )}
            {task && canLogActivitySession && !isManagingFields && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (missingRequiredFields.length > 0) {
                    setFormError(`Fill ${missingRequiredLabel} before logging a session for this activity.`);
                    return;
                  }
                  setIsLoggingSession(true);
                }}
                disabled={isSaving || missingRequiredFields.length > 0}
                title={missingRequiredFields.length > 0 ? `Missing ${missingRequiredLabel}` : 'Log activity session'}
              >
                <Timer className="mr-2 h-4 w-4" />
                Log Activity Session
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            {!isManagingFields && (
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Activity
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <ActivitySessionLogger
      task={task}
      open={isLoggingSession && canLogActivitySession}
      onClose={() => setIsLoggingSession(false)}
      onLogged={updatedTask => {
        updateTask(updatedTask.id, updatedTask);
        setIsLoggingSession(false);
        onClose();
      }}
    />
    </>
  );
}

function hasUsableDate(value?: string | null): boolean {
  if (!value) return false;
  const date = new Date(value);
  return Number.isFinite(date.getTime());
}

function dateTimeLocalValue(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function dateTimeLocalToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

function addMinutesIso(value: string, minutes: number): string | null {
  const start = new Date(value).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(minutes)) return null;
  return new Date(start + minutes * 60000).toISOString();
}

function normalizeDependencyIds(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return normalizeDependencyIds(parsed);
    } catch {
      return value
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);
    }
  }
  return [];
}

function StateScoreField({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: number | null;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <Input
        type="number"
        min={0}
        max={10}
        step={0.5}
        value={value ?? ''}
        onChange={event => onChange(event.target.value)}
        placeholder="0-10"
      />
    </div>
  );
}

function deriveProfileUpdate(
  profile: TaskStateProfile | undefined,
  key: StateScoreKey,
  value: string
): TaskStateProfile {
  const nextProfile: TaskStateProfile = {
    ...(profile || {}),
    [key]: value === '' ? null : Math.max(0, Math.min(10, Number(value))),
  };
  return {
    ...nextProfile,
    mode: deriveTaskStateMode(nextProfile),
  };
}

type StateScoreKey = Exclude<keyof TaskStateProfile, 'mode'>;

function formatMode(mode: NonNullable<TaskStateProfile['mode']>): string {
  switch (mode) {
    case 'productive':
      return 'Productive';
    case 'rejuvenating':
      return 'Rejuvenating';
    case 'both':
      return 'Both';
    default:
      return 'Neutral';
  }
}
